import { PanelHeader } from "components/Panel/styles";
import { LeaderboardPart } from "model/Leaderboard/User/SegmentLeaderboardUser";
import { getNumberPick, Pick } from "model/Pickem/Pickem";
import { PointsPartition } from "model/PickemDetails/PointsPartition";
import { getRankemPositionColumnText } from "model/PickemDetails/Rankem/PointsRules";
import { selectRosterDisplayName } from "selectors/Roster";
import { CenterFlexColumn, FlexRow } from "styles/layout";
import {
  GroupTable,
  GroupHeaderRow,
  GroupHeader,
  GroupRow,
  GroupCell,
  GroupOrgLogoContainer,
  GroupOrgName,
} from "./styles";
import { RosterOrgLogo } from "components/Logo/RosterOrgLogo";
import { useAppSelector } from "app/hooks";

interface Props {
  picks: {
    [id: string]: Pick;
  };
  leaderboardPart: LeaderboardPart | undefined;
  columns: PointsPartition[];
  groupRosterIds: string[];
  tournamentId: string;
  name?: string;
}

export const PickemUserRankemTable = ({
  picks,
  leaderboardPart,
  columns,
  groupRosterIds,
  tournamentId,
  name,
}: Props) => {
  const rosterIdsByPickOrder = Object.keys(picks)
    .filter((id) => groupRosterIds.includes(id))
    .sort(
      (a, b) =>
        (getNumberPick(picks, a) ?? Number.MAX_VALUE) -
        (getNumberPick(picks, b) ?? Number.MAX_VALUE)
    );

  return (
    <div>
      <CenterFlexColumn>
        {!!name && <PanelHeader>{name}</PanelHeader>}
        <GroupTable>
          <thead>
            <GroupHeaderRow>
              <th />
              <GroupHeader>Predicted</GroupHeader>
              <GroupHeader>Points</GroupHeader>
            </GroupHeaderRow>
          </thead>
          <tbody>
            {rosterIdsByPickOrder.map((rosterId) => (
              <GroupRow key={rosterId}>
                <PickemUserGroupPicksGroupTeamCell
                  rosterId={rosterId}
                  tournamentId={tournamentId}
                />
                <GroupCell>
                  {getPickPosText(rosterId, picks, columns)}
                </GroupCell>
                <GroupCell>
                  {getTeamPointsText(rosterId, leaderboardPart)}
                </GroupCell>
              </GroupRow>
            ))}
          </tbody>
        </GroupTable>
      </CenterFlexColumn>
    </div>
  );
};

const getTeamPointsText = (
  rosterId: string,
  leaderboardPart: LeaderboardPart | undefined
) => {
  if (!leaderboardPart) {
    return "-";
  }

  const teamPoints = leaderboardPart.points[rosterId];
  if (teamPoints === undefined) {
    return "-";
  }

  return teamPoints.toString();
};

const getPickPosText = (
  rosterId: string,
  picks: {
    [id: string]: Pick;
  },
  columns: PointsPartition[]
) => {
  const pickPos = getNumberPick(picks, rosterId);
  if (!pickPos) {
    return undefined;
  }

  return getRankemPositionColumnText(pickPos, columns) ?? "???";
};

interface TeamCellProps {
  rosterId: string;
  tournamentId: string;
}

const PickemUserGroupPicksGroupTeamCell = ({
  rosterId,
  tournamentId,
}: TeamCellProps) => {
  const displayName = useAppSelector((state) =>
    selectRosterDisplayName(state, rosterId, tournamentId)
  );

  return (
    <td>
      <FlexRow>
        <GroupOrgLogoContainer>
          <RosterOrgLogo rosterId={rosterId} tournamentId={tournamentId} />
        </GroupOrgLogoContainer>
        <GroupOrgName>{displayName}</GroupOrgName>
      </FlexRow>
    </td>
  );
};
